import { useFormik } from "formik";
import { FC, useState } from "react"
import { useIntl } from "react-intl";
import * as Yup from 'yup';
import useAccessToken from "../../../hooks/AccessToken";
import useGetTemplateCustomField from "../../../hooks/useGetTemplateCustomField";
import { CustomField, SettingMultiPipelines } from "../../../interfaces/Settings";
import { SelectOption, SelectOptionWithIcon } from "../../../interfaces/SelectOption";
import Swal from "sweetalert2";
import clsx from "clsx";
import { updateTemplateSettingPipeline } from "../../../api/SettingCRUD";
import { usePipelineStore } from "../../../stores/PipelineStore";
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { RowCustomField } from "../RowCustomField";
import { GroupBase } from "react-select";

interface Props {
    data: SettingMultiPipelines
    dataPipeline: SelectOption[]
    dataMerchant: SelectOption[]
    dataMeeting: SelectOption[]
    optionType: GroupBase<SelectOptionWithIcon>[]
    optionsResetSerial: SelectOption[]
}

const CustomFieldPipeline: FC<Props> = ({ data, dataPipeline, dataMerchant, dataMeeting, optionType, optionsResetSerial }) => {
    const intl = useIntl()
    const token = useAccessToken()
    const { multiPipelines, setMultiPipelines } = usePipelineStore()
    const [isLoading, setIsLoading] = useState(false)

    const formik = useFormik({
        initialValues: {
            value: '',
        },
        validationSchema: Yup.object().shape({
            value: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            let isDuplicate = false
            let isNameColumnEmpty = false
            let isOptionEmpty = false

            Object.keys(valueMultiCustomField).map((key1) => {
                if (valueMultiCustomField[key1].type === "option" || valueMultiCustomField[key1].type === "multiple" || valueMultiCustomField[key1].type === "multipleOption") {
                    if ((valueMultiCustomField[key1].value as string[]).includes("")) {
                        isOptionEmpty = true
                    }
                }

                if (valueMultiCustomField[key1].name === "") {
                    isNameColumnEmpty = true
                }

                Object.keys(valueMultiCustomField).map((key2) => {
                    if ((valueMultiCustomField[key1].name)?.trim() === (valueMultiCustomField[key2].name)?.trim() && key1 !== key2) {
                        isDuplicate = true
                    }
                })
            })

            if (isOptionEmpty) return false
            if (isNameColumnEmpty) return false
            if (isDuplicate) {
                Swal.fire({
                    title: intl.formatMessage({ id: "FORM.VALIDATION.DUPLICATE" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME_COLUMN" }) }),
                    icon: 'error',
                    confirmButtonText: 'Ok',
                    heightAuto: false
                })
                return false
            }

            setIsLoading(true)

            try {
                const res =
                    await updateTemplateSettingPipeline(
                        token,
                        data.id,
                        data.name,
                        data.currencyId,
                        data.merchantTemplates?.map((item) => item.id) || [],
                        data.roles?.map((item) => item.id) || [],
                        data.progress?.map((item) => item.id) || [],
                        data.product?.map((item) => item.id) || [],
                        modifiedObject,
                        data.isFeatureLabelEnabled,
                        data.isFeatureCogsEnabled,
                        data.leadSources?.map((item) => item.id) || [],
                        data.allowOptionalProducts,
                        data.isInfiniteLog,
                        data.isAllowAfterFailedLog,
                        data.isAllowAfterSuccessLog,
                        data.sourceFilterDate,
                        data.filterDate,
                        data.variantPriceId,
                        data.isDisabledPriceProduct
                    )
                if (res.data.success) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: intl.formatMessage({ id: "FORM.VALIDATION.SAVE_SUCCESS" }),
                        timer: 2000,
                        timerProgressBar: true
                    })
                    Object.values(valueMultiCustomField).forEach(field => {
                        field.new = false;
                    });

                    setMultiPipelines(multiPipelines.map(item => item.id === data.id ? { ...res.data.data, label: res.data.data.name, value: res.data.data.id } : item));
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.respons.data.message,
                    confirmButtonText: 'Ok'
                })
            } finally {
                setIsLoading(false)
            }
        }
    });

    const { valueMultiCustomField, keysMultiCustomField, setValueMultiCustomField, setKeysMultiCustomField, modifiedObject } = useGetTemplateCustomField({ data, dataPipeline, dataMerchant, dataMeeting, formikCustomColumn: formik, type: "Database Pipeline" })

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const updateColumns = (field: CustomField) => {
                const keys = Object.keys(field);
                const oldIndex = keys.indexOf(active.id);
                const newIndex = keys.indexOf(over.id);

                const newKeys = arrayMove(keys, oldIndex, newIndex);

                const newField: CustomField = {};
                newKeys.forEach(key => {
                    newField[key] = field[key];
                });

                return newField;
            };

            const newColumns = updateColumns(valueMultiCustomField);
            setValueMultiCustomField(newColumns);
        }
    };

    const addColumn = () => {
        var key = crypto.randomUUID()
        const addColumnPipeline = { ...valueMultiCustomField, [key]: { name: `${intl.formatMessage({ id: "FORM.LABEL.COLUMN" })} ` + (Object.keys(valueMultiCustomField).length + 1), type: 'text', required: false, value: [], progress: [], score: "", new: true } };
        setValueMultiCustomField(addColumnPipeline);
        setKeysMultiCustomField([...keysMultiCustomField, key])
    }

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <div className="card-body p-0">
                {
                    <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
                        <SortableContext items={Object.keys(valueMultiCustomField) as any} strategy={verticalListSortingStrategy}>
                            {
                                Object.keys(valueMultiCustomField).map((key, index) => {
                                    if (valueMultiCustomField[key].type !== "invoice_reminder") {
                                        return (
                                            <RowCustomField
                                                key={key}
                                                id={key}
                                                type="pipeline"
                                                customField={valueMultiCustomField}
                                                index={index}
                                                optionType={optionType}
                                                optionsResetSerial={optionsResetSerial}
                                                setCustomField={setValueMultiCustomField}
                                                dataPipeline={dataPipeline}
                                                dataMerchant={dataMerchant}
                                                dataMeeting={dataMeeting}
                                                dataProgressByTemplateId={data.progress}
                                            />
                                        )
                                    }
                                })
                            }
                        </SortableContext>
                    </DndContext>
                }
            </div>
            <div className="card-footer py-6 px-9 mt-6">
                <div className="row">
                    <label className="col-xl-3"></label>
                    <div className="col-lg-9 col-xl-9" style={{ display: "flex", justifyContent: "space-between" }}>
                        <button type="button" className="btn btn-secondary" onClick={() => addColumn()}>
                            <i className="fas fa-plus"></i>
                            {intl.formatMessage({ id: "FORM.ACTION.ADD" })} {intl.formatMessage({ id: "FORM.LABEL.COLUMN" })}
                        </button>
                        <button type="submit" className={`btn btn-primary ${isLoading ? "disabled" : ""}`} data-kt-indicator={isLoading ? 'on' : 'off'}>
                            <span className="indicator-label">
                                {intl.formatMessage({ id: "FORM.ACTION.SAVE" })}
                            </span>
                            <span className="indicator-progress">
                                Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </form>
    )
}

export { CustomFieldPipeline }